import React, { useState, useRef, useCallback, useEffect } from 'react';
import { extractPDFAreaFromPage, getPDFPageCount } from '../../utils/pdfExtractor';
import { useLanguage } from '../../contexts/LanguageContext';
import * as pdfjs from 'pdfjs-dist';

const PDFViewer = ({ pdfUrl, selectedPieces, setSelectedPieces, onError }) => {
  const { t } = useLanguage();
  const [isSelecting, setIsSelecting] = useState(false);
  const [currentSelection, setCurrentSelection] = useState(null);
  const [scale, setScale] = useState(1);
  const [pdfError, setPdfError] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270 degrees

  // No global body class needed - we'll handle touch prevention at component level
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isExtracting, setIsExtracting] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [overlayKey, setOverlayKey] = useState(0); // Force overlay re-render when canvas size changes
  const [scrollPosition, setScrollPosition] = useState(0); // Track scroll for overlay repositioning
  const [containerRect, setContainerRect] = useState(null); // Track container position for WebView overlay
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 }); // Track page dimensions for dynamic zoom
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // Load PDF document (only when pdfUrl changes, not on every render)
  useEffect(() => {
    const loadPDF = async () => {
      if (!pdfUrl) {
        setPdfDoc(null);
        setTotalPages(1);
        setCurrentPage(1);
        setRotation(0); // Reset rotation when no PDF
        return;
      }

      try {
        setIsLoading(true);
        setPdfError(false);

        console.log('Loading PDF (new URL):', pdfUrl);

        // Determine document URL (local vs remote)
        let documentUrl = pdfUrl;
        if (!pdfUrl.startsWith('blob:')) {
          // For remote URLs, you might want to use a proxy
          // documentUrl = createProxiedUrl(pdfUrl);
        }

        const loadingTask = pdfjs.getDocument(documentUrl);
        const pdf = await loadingTask.promise;

        const prevPdfDoc = pdfDoc;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        console.log('PDF loaded successfully. Pages:', pdf.numPages);

        // Always reset rotation to 0 for any PDF load
        console.log('PDF loaded, resetting rotation to 0');
        setRotation(0);

        // Only reset to page 1 for a completely new PDF (not just a reload)
        if (!prevPdfDoc) {
          console.log('New PDF loaded, setting to page 1');
          setCurrentPage(1);
        } else {
          console.log('PDF reloaded, maintaining current page');
          // Keep current page if it's valid for the new PDF
          setCurrentPage(prev => {
            const newPage = prev <= pdf.numPages ? prev : 1;
            console.log('Maintaining page:', newPage);
            return newPage;
          });
        }

      } catch (error) {
        console.error('Error loading PDF:', error);
        setPdfError(true);
        if (onError) {
          onError('PDF failed to load');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPDF();
  }, [pdfUrl]); // Removed onError from dependencies to prevent unnecessary reloads

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      console.log('🎨 renderPage effect fired', {
        pdfDoc: !!pdfDoc,
        canvasRef: !!canvasRef.current,
        currentPage,
        totalPages,
        scale,
        rotation,
        isLoading
      });

      if (!pdfDoc) {
        console.log('❌ No pdfDoc available');
        return;
      }

      if (isLoading) {
        console.log('⏳ Still loading, waiting...');
        return;
      }

      if (!canvasRef.current) {
        console.log('❌ No canvas ref available, retrying...');
        // Wait a bit for the canvas to mount
        setTimeout(() => {
          if (canvasRef.current && pdfDoc) {
            console.log('✅ Canvas now available, triggering re-render');
            // Trigger the effect again by updating a dummy state
            setScale(s => s);
          }
        }, 100);
        return;
      }

      if (currentPage < 1 || currentPage > totalPages) {
        console.log('❌ Invalid page number', currentPage, 'of', totalPages);
        return;
      }

      try {
        console.log('✅ Starting page render:', currentPage);

        const page = await pdfDoc.getPage(currentPage);
        console.log('✅ Got page object');

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Get page dimensions at scale 1 to determine zoom range
        const baseViewport = page.getViewport({ scale: 1, rotation });
        setPageDimensions({ width: baseViewport.width, height: baseViewport.height });
        console.log('📐 Page base dimensions:', { width: baseViewport.width, height: baseViewport.height });

        // Calculate viewport with rotation
        console.log('🔍 Rendering with rotation:', rotation, 'degrees', 'scale:', scale);
        const viewport = page.getViewport({ scale, rotation });

        console.log('📏 Canvas dimensions will be:', {
          width: viewport.width,
          height: viewport.height,
          scale: scale
        });

        // Set canvas internal dimensions (affects rendering resolution)
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // IMPORTANT: Canvas has two dimensions:
        // 1. Internal dimensions (canvas.width/height) - affects rendering quality
        // 2. CSS dimensions (style.width/height) - affects display size
        // They should match for 1:1 pixel rendering without scaling
        canvas.style.width = viewport.width + 'px';
        canvas.style.height = viewport.height + 'px';

        console.log('📏 Canvas element size set to:', canvas.width, 'x', canvas.height);

        // Clear canvas
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Render page
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        console.log('🎨 Starting render...');
        await page.render(renderContext).promise;
        console.log('✅✅✅ Page rendered successfully!');

        // Force overlay to update after canvas size changes
        setOverlayKey(prev => prev + 1);

      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pdfDoc, currentPage, scale, rotation, totalPages, isLoading]);

  // Add scroll listener to update overlay position
  useEffect(() => {
    if (selectionMode) {
      const handleScroll = () => {
        console.log('[Scroll Event] Scroll detected, updating positions');
        setScrollPosition(window.scrollY);
        // Update container rect for WebView overlay positioning
        if (window.isReactNativeWebView && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          console.log('[Scroll Event] New container rect:', rect);
          setContainerRect(rect);
        }
      };

      // Initial position
      if (window.isReactNativeWebView && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        console.log('[Initial] Setting initial container rect:', rect);
        setContainerRect(rect);
      }

      // For WebView: Use requestAnimationFrame to continuously track position
      let animationFrameId;
      if (window.isReactNativeWebView) {
        const updatePosition = () => {
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setContainerRect(rect);
          }
          animationFrameId = requestAnimationFrame(updatePosition);
        };
        animationFrameId = requestAnimationFrame(updatePosition);
      }

      // Listen to scroll on multiple targets to catch all scroll events
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true });

      // Also listen on body for WebView compatibility
      if (document.body) {
        document.body.addEventListener('scroll', handleScroll, { passive: true });
      }

      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
        if (document.body) {
          document.body.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [selectionMode]);

  const handlePdfError = () => {
    setPdfError(true);
    if (onError) {
      onError('PDF failed to load');
    }
  };

  const startSelection = useCallback((e) => {
    if (!containerRef.current || !canvasRef.current) return;

    // Only prevent default behavior during selection to avoid scrolling conflicts
    if (selectionMode) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (clientX === undefined || clientY === undefined) return;

    // Get coordinates relative to the canvas
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const rawX = clientX - canvasRect.left;
    const rawY = clientY - canvasRect.top;

    // Convert from display coordinates to canvas intrinsic coordinates
    // Canvas might be scaled by CSS, so we need to map display coords to canvas coords
    const canvas = canvasRef.current;
    const scaleX = canvas.width / canvasRect.width;
    const scaleY = canvas.height / canvasRect.height;

    const x = rawX * scaleX;
    const y = rawY * scaleY;

    console.log('Selection started:', {
      eventType: e.type,
      eventCoords: { clientX, clientY },
      canvasRect: { left: canvasRect.left, top: canvasRect.top, width: canvasRect.width, height: canvasRect.height },
      canvasIntrinsic: { width: canvas.width, height: canvas.height },
      rawCoords: { rawX, rawY },
      cssScale: { scaleX, scaleY },
      finalCoords: { x, y },
      currentScale: scale
    });

    setIsSelecting(true);
    setCurrentSelection({
      startX: x,
      startY: y,
      endX: x,
      endY: y
    });
  }, [scale, selectionMode]);

  const updateSelection = useCallback((e) => {
    if (!isSelecting || !containerRef.current || !canvasRef.current) return;

    // Only prevent default during active selection
    if (isSelecting) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (clientX === undefined || clientY === undefined) return;

    // Get coordinates relative to the canvas
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const rawX = clientX - canvasRect.left;
    const rawY = clientY - canvasRect.top;

    // Convert from display coordinates to canvas intrinsic coordinates
    const canvas = canvasRef.current;
    const scaleX = canvas.width / canvasRect.width;
    const scaleY = canvas.height / canvasRect.height;

    const x = rawX * scaleX;
    const y = rawY * scaleY;

    setCurrentSelection(prev => ({
      ...prev,
      endX: x,
      endY: y
    }));
  }, [isSelecting]);

  const extractPDFArea = useCallback(async (x, y, width, height) => {
    try {
      setIsExtracting(true);
      console.log('Extracting PDF area - input:', { x, y, width, height, currentPage, scale });
      console.log('Canvas dimensions:', canvasRef.current ? {
        width: canvasRef.current.width,
        height: canvasRef.current.height
      } : 'Canvas not available');

      // Use PDF.js to extract high-resolution content from the current page
      // Convert canvas coordinates back to PDF base coordinates (scale = 1)
      const basePdfX = x / scale;
      const basePdfY = y / scale;
      const basePdfWidth = width / scale;
      const basePdfHeight = height / scale;

      console.log('Converting to base PDF coordinates:', {
        canvasCoords: { x, y, width, height },
        scale,
        basePdfCoords: { basePdfX, basePdfY, basePdfWidth, basePdfHeight }
      });

      const extractedImage = await extractPDFAreaFromPage(
        pdfUrl,
        currentPage,
        basePdfX, // Base PDF coordinates (normalized to scale = 1)
        basePdfY,
        basePdfWidth,
        basePdfHeight,
        1, // Base scale - coordinates are already normalized
        8, // Ultra-high resolution scale factor for maximum quality (8x)
        rotation // Pass the current rotation
      );

      if (extractedImage) {
        console.log('PDF extraction successful:', extractedImage.substring(0, 50) + '...');
        return extractedImage;
      } else {
        console.log('PDF extraction failed, creating fallback');
        // Create a simple fallback if extraction fails
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width * 2;
        canvas.height = height * 2;

        // Gradient background
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#e3f2fd');
        gradient.addColorStop(1, '#bbdefb');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Text
        ctx.fillStyle = '#1976d2';
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('PDF Content', canvas.width / 2, canvas.height / 2);

        return canvas.toDataURL('image/png', 1.0);
      }
    } catch (error) {
      console.error('Error extracting PDF area:', error);

      // Fallback canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = width * 2;
      canvas.height = height * 2;

      ctx.fillStyle = '#ffcccb';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#000';
      ctx.font = '20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Extraction Error', canvas.width / 2, canvas.height / 2);

      return canvas.toDataURL('image/png', 1.0);
    } finally {
      setIsExtracting(false);
    }
  }, [pdfUrl, currentPage, scale, rotation]);

  const finishSelection = useCallback(async (e) => {
    if (!isSelecting || !currentSelection) return;

    // Only prevent default during active selection
    if (e && isSelecting) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Capture the selection coordinates before clearing state
    const { startX, startY, endX, endY } = currentSelection;
    const minX = Math.min(startX, endX);
    const minY = Math.min(startY, endY);
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    // IMPORTANT: Clear selection state immediately to hide the rectangle
    // This prevents visual issues when the page scrolls during extraction
    setIsSelecting(false);
    setCurrentSelection(null);

    if (width > 20 && height > 20) {
      // Extract high-resolution PDF content from the selected area
      // This happens in the background after the rectangle is already hidden
      const extractedImage = await extractPDFArea(minX, minY, width, height);
      console.log('PDF extraction result:', extractedImage ? 'Success' : 'Failed');

      const newArea = {
        id: Date.now(),
        x: minX,
        y: minY,
        width,
        height,
        pdfUrl,
        scale,
        pageNumber: currentPage,
        extractedImage: extractedImage // Add the high-resolution PDF extraction
      };

      console.log('New area created:', newArea);

      setSelectedPieces(prev => [...prev, newArea]);
    }
  }, [isSelecting, currentSelection, pdfUrl, setSelectedPieces, scale, rotation, extractPDFArea, currentPage]);

  const removeArea = (areaId) => {
    setSelectedPieces(prev => prev.filter(area => area.id !== areaId));
  };

  // Calculate dynamic zoom limits based on PDF dimensions
  const getZoomLimits = () => {
    if (!pageDimensions.width || !pageDimensions.height) {
      // Default values if dimensions not available yet
      return { minScale: 0.1, maxScale: 8, step: 0.25 };
    }

    const pageWidth = pageDimensions.width;
    const pageHeight = pageDimensions.height;
    const maxDimension = Math.max(pageWidth, pageHeight);
    const minDimension = Math.min(pageWidth, pageHeight);

    // Calculate zoom limits based on page size
    // For very large pages (e.g., architectural drawings), allow zooming out more
    // For small pages, allow zooming in more
    let minScale = 0.1; // Default minimum
    let maxScale = 8;   // Default maximum
    let step = 0.25;    // Default step

    // Adjust based on page dimensions
    if (maxDimension > 2000) {
      // Very large PDF (e.g., poster, architectural drawing)
      minScale = 0.05;  // Allow zooming out to 5%
      maxScale = 12;    // Allow zooming in to 1200%
      step = 0.1;       // Finer control for large documents
    } else if (maxDimension > 1200) {
      // Large PDF
      minScale = 0.1;
      maxScale = 10;
      step = 0.25;
    } else if (maxDimension < 600) {
      // Small PDF
      minScale = 0.25;
      maxScale = 15;    // Allow more zoom for small documents
      step = 0.5;
    } else {
      // Medium PDF (standard size)
      minScale = 0.1;
      maxScale = 8;
      step = 0.25;
    }

    console.log('🔍 Zoom limits calculated:', {
      pageDimensions: { width: pageWidth, height: pageHeight },
      maxDimension,
      minDimension,
      limits: { minScale, maxScale, step }
    });

    return { minScale, maxScale, step };
  };

  const getSelectionStyle = () => {
    if (!currentSelection || !canvasRef.current || !containerRef.current) return {};

    const { startX, startY, endX, endY } = currentSelection;
    const intrinsicLeft = Math.min(startX, endX);
    const intrinsicTop = Math.min(startY, endY);
    const intrinsicWidth = Math.abs(endX - startX);
    const intrinsicHeight = Math.abs(endY - startY);

    // Convert from canvas intrinsic coordinates back to display coordinates for positioning
    const canvas = canvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();

    const displayScaleX = canvasRect.width / canvas.width;
    const displayScaleY = canvasRect.height / canvas.height;

    const displayLeft = intrinsicLeft * displayScaleX;
    const displayTop = intrinsicTop * displayScaleY;
    const displayWidth = intrinsicWidth * displayScaleX;
    const displayHeight = intrinsicHeight * displayScaleY;

    // Use fixed positioning relative to viewport (like the overlay)
    // Position relative to the canvas's position on screen
    const left = canvasRect.left + displayLeft;
    const top = canvasRect.top + displayTop;

    console.log('Selection box positioning:', {
      displayCoords: { left: displayLeft, top: displayTop, width: displayWidth, height: displayHeight },
      canvasRect: { left: canvasRect.left, top: canvasRect.top },
      finalPosition: { left, top }
    });

    return {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      border: '3px dashed #007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.2)',
      pointerEvents: 'none',
      zIndex: 10000
    };
  };

  return (
    <div className="pdf-viewer">
      <div className="selected-pieces-info">
        <p>{t('selectedAreas')}: {selectedPieces.length}</p>
        <p>
          {isExtracting
            ? t('extracting')
            : selectionMode
            ? t('clickDragSelect')
            : t('navigateMode')
          }
        </p>
        {selectedPieces.length > 0 && (
          <p>{t('goToCompose')}</p>
        )}
        {selectedPieces.length > 0 && (
          <div className="pieces-list">
            {selectedPieces.map((area, index) => (
              <div key={area.id} className="piece-item">
                <div className="piece-thumbnail">
                  <div
                    className="thumbnail-box"
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #007bff',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    {area.extractedImage ? (
                      <img
                        src={area.extractedImage}
                        alt={`Area ${index + 1} (Page ${area.pageNumber})`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onLoad={() => console.log('Thumbnail image loaded successfully')}
                        onError={() => console.error('Thumbnail image failed to load')}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        color: '#666'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div>📄</div>
                          <div style={{ fontSize: '0.6rem' }}>
                            {Math.round(area.width)}×{Math.round(area.height)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="piece-info">
                  <span className="piece-title">{t('area')} {index + 1}</span>
                  <span className="piece-details">
                    {t('page')} {area.pageNumber || 1} • {Math.round(area.width)}×{Math.round(area.height)}px
                  </span>
                </div>
                <button onClick={() => removeArea(area.id)} className="remove-btn">
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pdf-controls" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px 8px',
        marginBottom: '12px',
        alignItems: 'center'
      }}>
        {/* First row: Rotation and Page controls */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Rotation controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <button
              onClick={() => setRotation(prev => (prev - 90 + 360) % 360)}
              style={{
                padding: '0',
                fontSize: '18px',
                width: '44px',
                height: '44px',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: '#2196F3',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ↺
            </button>
            <span style={{
              fontSize: '13px',
              fontWeight: 'bold',
              width: '45px',
              textAlign: 'center'
            }}>
              {rotation}°
            </span>
            <button
              onClick={() => setRotation(prev => (prev + 90) % 360)}
              style={{
                padding: '0',
                fontSize: '18px',
                width: '44px',
                height: '44px',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: '#2196F3',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ↻
            </button>
          </div>
          {/* Page controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              style={{
                padding: '0',
                fontSize: '16px',
                width: '44px',
                height: '44px',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: currentPage <= 1 ? '#e0e0e0' : '#2196F3',
                color: currentPage <= 1 ? '#999' : 'white',
                fontWeight: 'bold',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ←
            </button>
            <span style={{
              fontSize: '13px',
              fontWeight: 'bold',
              width: '45px',
              textAlign: 'center'
            }}>
              {currentPage}/{totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              style={{
                padding: '0',
                fontSize: '16px',
                width: '44px',
                height: '44px',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: currentPage >= totalPages ? '#e0e0e0' : '#2196F3',
                color: currentPage >= totalPages ? '#999' : 'white',
                fontWeight: 'bold',
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              →
            </button>
          </div>
        </div>

        {/* Second row: Zoom and Mode toggle */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Zoom controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <button
              onClick={() => {
                const { minScale, step } = getZoomLimits();
                console.log('🔍 Zoom Out clicked, current scale:', scale, 'limits:', { minScale, step });
                setScale(prev => {
                  const newScale = Math.max(minScale, prev - step);
                  console.log('🔍 New scale will be:', newScale);
                  return newScale;
                });
              }}
              disabled={scale <= getZoomLimits().minScale}
              style={{
                padding: '0',
                fontSize: '18px',
                width: '44px',
                height: '44px',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: scale <= getZoomLimits().minScale ? '#e0e0e0' : '#2196F3',
                color: scale <= getZoomLimits().minScale ? '#999' : 'white',
                fontWeight: 'bold',
                cursor: scale <= getZoomLimits().minScale ? 'not-allowed' : 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              −
            </button>
            <span style={{
              fontSize: '13px',
              fontWeight: 'bold',
              width: '45px',
              textAlign: 'center'
            }}>
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => {
                const { maxScale, step } = getZoomLimits();
                console.log('🔍 Zoom In clicked, current scale:', scale, 'limits:', { maxScale, step });
                setScale(prev => {
                  const newScale = Math.min(maxScale, prev + step);
                  console.log('🔍 New scale will be:', newScale);
                  return newScale;
                });
              }}
              disabled={scale >= getZoomLimits().maxScale}
              style={{
                padding: '0',
                fontSize: '18px',
                width: '44px',
                height: '44px',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: scale >= getZoomLimits().maxScale ? '#e0e0e0' : '#2196F3',
                color: scale >= getZoomLimits().maxScale ? '#999' : 'white',
                fontWeight: 'bold',
                cursor: scale >= getZoomLimits().maxScale ? 'not-allowed' : 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              +
            </button>
          </div>

          {/* Mode toggle button */}
          <button
            onClick={() => setSelectionMode(!selectionMode)}
            className={`mode-btn ${selectionMode ? 'active' : ''}`}
            style={{
              padding: '10px 0',
              fontSize: '16px',
              minHeight: '44px',
              width: '149px',
              border: '2px solid #2196F3',
              borderRadius: '22px',
              backgroundColor: selectionMode ? '#2196F3' : 'white',
              color: selectionMode ? 'white' : '#2196F3',
              fontWeight: 'bold',
              cursor: 'pointer',
              touchAction: 'manipulation',
              userSelect: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            {selectionMode ? t('selectionMode') : t('navigateButton')}
          </button>
        </div>
      </div>

      <div className={`pdf-container-wrapper ${selectionMode ? 'selection-active' : ''}`}>
        {pdfError ? (
          <div className="pdf-error">
            <p>{t('couldNotLoad')}</p>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="pdf-link">
              {t('openInNewTab')}
            </a>
            <p>{t('canStillUse')}</p>
          </div>
        ) : null}

        <div
          className="pdf-container"
          ref={containerRef}
          style={{
            position: 'relative',
            minHeight: '600px',
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start', // Changed from 'center' to allow proper left scrolling
            alignItems: 'flex-start',
            overflow: selectionMode ? 'hidden' : 'auto', // Disable scroll in selection mode
            maxHeight: '80vh', // Limit height so it doesn't take up entire screen
            padding: '20px' // Add padding to center content visually while allowing scroll
          }}
        >
          {isLoading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
              fontSize: '1.1rem',
              color: '#666'
            }}>
              {t('loadingPdf')}
            </div>
          ) : pdfDoc ? (
            <canvas
              ref={canvasRef}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                display: 'block',
                minWidth: '100px',
                minHeight: '100px',
                backgroundColor: 'white',
                maxWidth: 'none',
                flexShrink: 0
              }}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
              fontSize: '1.1rem',
              color: '#666'
            }}>
              {t('uploadToStart')}
            </div>
          )}

          {/* Overlay covering only the visible viewport area in selection mode */}
          {selectionMode && containerRef.current && (
            <div
              key={`${overlayKey}-${scrollPosition}`}
              className="selection-overlay"
              style={
                window.isReactNativeWebView && containerRect
                  ? {
                      // WebView: use fixed positioning with updated rect
                      position: 'fixed',
                      top: containerRect.top + 'px',
                      left: containerRect.left + 'px',
                      width: containerRect.width + 'px',
                      height: containerRect.height + 'px',
                      backgroundColor: 'rgba(0,123,255,0.25)',
                      cursor: 'crosshair',
                      zIndex: 9999,
                      pointerEvents: 'auto'
                    }
                  : {
                      // Regular browser: use absolute positioning offset by scroll position
                      position: 'absolute',
                      top: containerRef.current.scrollTop + 'px',
                      left: containerRef.current.scrollLeft + 'px',
                      width: containerRef.current.clientWidth + 'px',
                      height: containerRef.current.clientHeight + 'px',
                      backgroundColor: 'rgba(0,123,255,0.25)',
                      cursor: 'crosshair',
                      zIndex: 9999,
                      pointerEvents: 'auto'
                    }
              }
              onMouseDown={startSelection}
              onMouseMove={updateSelection}
              onMouseUp={finishSelection}
              onMouseLeave={finishSelection}
              onTouchStart={startSelection}
              onTouchMove={updateSelection}
              onTouchEnd={finishSelection}
            />
          )}

          {isSelecting && currentSelection && (
            <div style={getSelectionStyle()} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
